import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, startViewTransition } from '@theme/utilities';

/**
 * A custom element for the main blog section with infinite scroll pagination.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [grid] - The grid element containing blog articles.
 * @property {HTMLElement} [viewMoreNext] - The view more trigger element.
 * @property {HTMLElement[]} [articles] - The article card elements.
 *
 * @extends Component<Refs>
 */
export default class MainBlog extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  connectedCallback() {
    super.connectedCallback();

    this.#fetchPage('next');
    this.#observeViewMore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
  }

  #observeViewMore() {
    const { viewMoreNext } = this.refs;

    // Return if element doesn't exist
    if (!viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const { viewMoreNext } = this.refs;

              if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '200px',
        },
      );
    }

    // Observe the view more element
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageArticleElements = this.#getGridForPage(nextPage.page);

    if (!nextPageArticleElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageArticleElements = this.#getGridForPage(nextPage.page);
      if (!nextPageArticleElements) return;
    }

    grid.append(...nextPageArticleElements);

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * @param {"next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { articles } = this.refs;

    if (!Array.isArray(articles)) return;

    const targetArticle = articles[articles.length - 1];

    if (!targetArticle) return;

    const currentArticlePage = Number(targetArticle.dataset.page);
    const page = currentArticlePage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="articles[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Clears all filter URL parameters and re-renders the section.
   * Called when the "Clear filters" button is clicked in the empty state.
   */
  clearFilters() {
    const url = new URL(window.location.href);

    // Remove filter parameters
    url.searchParams.delete('tag');
    url.searchParams.delete('blog');
    url.searchParams.delete('page');

    // Update browser history
    history.pushState({}, '', url.toString());

    // Re-render the section with view transition
    startViewTransition(
      () => sectionRenderer.renderSection(this.sectionId, { url, cache: false }),
      ['article-grid'],
    );
  }
}

if (typeof customElements !== 'undefined') {
  customElements.define('main-blog', MainBlog);
}
